/**
 * rentalStatusLabel.ts — Single Source of Truth สำหรับ "ป้ายสถานะการเช่า" (ภาษาไทย + สี badge)
 *
 * ใช้ร่วมกันทุกหน้าที่แสดงสถานะ rental (List / Detail / Dashboard / Report) เพื่อกัน
 * bug class "สถานะโผล่เป็น raw enum (เช่น PENDING_CANCELLATION)" เพราะลืม map สถานะใดสถานะหนึ่ง
 *
 * ทุกค่าใน RentalStatus ต้องมี label ที่นี่ — มี vitest บังคับครบทุก enum
 * (ดู src/utils/__tests__/rentalStatusLabel.test.ts)
 */

import { RentalStatus } from '../services/rentalService';

export interface RentalStatusDisplay {
  label: string;
  /** สี badge แบบเต็ม (พื้นหลัง + ตัวอักษร) ใช้กับ rounded badge */
  badgeColor: string;
  /** สีตัวอักษรอย่างเดียว ใช้กับตาราง/inline text */
  textColor: string;
}

/** map สถานะ → การแสดงผล (label ภาษาไทยมาตรฐานทั้งระบบ) */
export const RENTAL_STATUS_DISPLAY: Record<RentalStatus, RentalStatusDisplay> = {
  [RentalStatus.DRAFT]: {
    label: 'ร่าง',
    badgeColor: 'bg-warning-100 text-warning-800',
    textColor: 'text-warning-700',
  },
  [RentalStatus.ACTIVE]: {
    label: 'เช่าใช้อยู่',
    badgeColor: 'bg-success-100 text-success-800',
    textColor: 'text-success-700',
  },
  [RentalStatus.PENDING_CANCELLATION]: {
    label: 'รอการยกเลิก',
    badgeColor: 'bg-warning-100 text-warning-800',
    textColor: 'text-warning-700',
  },
  [RentalStatus.COMPLETED]: {
    label: 'เสร็จสิ้น',
    badgeColor: 'bg-info-100 text-info-800',
    textColor: 'text-info-700',
  },
  [RentalStatus.CANCELLED]: {
    label: 'ยกเลิกแล้ว',
    badgeColor: 'bg-danger-100 text-danger-800',
    textColor: 'text-danger-700',
  },
};

/**
 * map สถานะ → ค่า query param `?status=` ที่หน้า List (RentalPage) รู้จัก
 * กัน drift: dashboard เคยลิงก์ `?status=pending` แต่ List รองรับเฉพาะ `pending_cancellation`
 * → การ์ดคลิกแล้วไม่กรอง (BUG-QA-002). ทุก enum ต้องมี param (มี test บังคับ)
 */
export const RENTAL_STATUS_URL_PARAM: Record<RentalStatus, string> = {
  [RentalStatus.DRAFT]: 'draft',
  [RentalStatus.ACTIVE]: 'active',
  [RentalStatus.PENDING_CANCELLATION]: 'pending_cancellation',
  [RentalStatus.COMPLETED]: 'completed',
  [RentalStatus.CANCELLED]: 'cancelled',
};

/** คืนค่า query param ของสถานะ (สำหรับสร้างลิงก์ไปหน้า List แบบกรองสถานะ) */
export function getRentalStatusUrlParam(status: RentalStatus): string {
  return RENTAL_STATUS_URL_PARAM[status];
}

const FALLBACK: RentalStatusDisplay = {
  label: '',
  badgeColor: 'bg-secondary-100 text-secondary-800',
  textColor: 'text-secondary-600',
};

/**
 * คืนการแสดงผลของสถานะ rental — รองรับทั้งตัวพิมพ์ใหญ่/เล็ก
 * ถ้าไม่รู้จักสถานะ → fallback (label = ค่าดิบที่ส่งมา เพื่อไม่ให้หน้าว่าง แต่จะถูกจับโดย test)
 */
export function getRentalStatusDisplay(status?: string | null): RentalStatusDisplay {
  const key = (status ?? '').toUpperCase() as RentalStatus;
  const found = RENTAL_STATUS_DISPLAY[key];
  if (found) return found;
  return { ...FALLBACK, label: status ?? '' };
}
